import { Box, Divider, Text, TextButton } from '@wix/design-system';
import { EllipsisForTrans } from '@wix/package-picker-shared';
import {
  STUDIO_DARK_COLOR,
  useIsStudioNewUi,
  useMonthlyDisabledFeatures,
  useRevShare,
} from '@wix/package-picker-shared-studio';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import { DismissSmall } from '@wix/wix-ui-icons-common/lazy';
import { DismissSmall as HarmonyDismissSmall } from '@wix/wix-ui-icons-common/odeditor';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';

interface CycleSelectorDisabledFeaturesProps {
  productId: string;
  selectCycle: (cycle: PurchaseOffering.PurchaseCycle) => void;
}

export const CycleSelectorDisabledFeatures = ({
  productId,
  selectCycle,
}: CycleSelectorDisabledFeaturesProps) => {
  const { data: disabledFeatures } = useMonthlyDisabledFeatures(productId);
  const { Trans } = StudioPackagePickerModule.internals.useTranslations();
  const { hasRevShare } = useRevShare();
  const isStudioNewUi = useIsStudioNewUi();
  const DismissIcon = isStudioNewUi ? HarmonyDismissSmall : DismissSmall;

  const title = hasRevShare ? (
    <Trans
      i18nKey="cycle_entry.monthly_disclaimer_revshare.note"
      components={{ strong: <Text size="small" weight="bold" /> }}
    />
  ) : (
    <Trans
      i18nKey="cycle_entry.monthly_disclaimer.note"
      components={[<Text size="small" weight="bold" />]}
    />
  );

  return (
    <Box
      marginTop="SP2"
      {...(isStudioNewUi
        ? { paddingLeft: '28px' }
        : { backgroundColor: '#f7f9fa', padding: 'SP4', borderRadius: '6px' })}
    >
      <Box direction="vertical" gap="SP2">
        <Box direction="vertical" gap="SP2">
          <Text size="small">{title}</Text>
          <Box direction="vertical" gap="SP1">
            {disabledFeatures.map((feature) => (
              <Box direction="horizontal" verticalAlign="middle" gap="SP1">
                <DismissIcon
                  color={isStudioNewUi ? STUDIO_DARK_COLOR : '#C8C7CC'}
                />
                <Text size="small">
                  <Trans
                    i18nKey={feature.content?.name?.translationKey}
                    values={{ domainName: undefined }} // TODO: add domain name
                    components={{
                      ellipsis: <EllipsisForTrans maxWidth={160} />,
                    }}
                  />
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
        <Divider />
        <Text size="small">
          <Trans
            i18nKey="cycle_entry.monthly_disclaimer.footer"
            components={{
              1: (
                <TextButton
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    StudioPackagePickerModule.internals.bi.onMonthlyCycleSectionHelperLinkClick?.(
                      { action: 'select-1year-cycle' },
                    );
                    e.stopPropagation();
                    selectCycle({
                      cycleType: PurchaseOffering.CycleType.RECURRING,
                      cycleDuration: {
                        unit: PurchaseOffering.DurationUnit.YEAR,
                        count: 1,
                      },
                    });
                  }}
                />
              ),
              3: (
                <TextButton
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    StudioPackagePickerModule.internals.bi.onMonthlyCycleSectionHelperLinkClick?.(
                      { action: 'select-2year-cycle' },
                    );
                    e.stopPropagation();
                    selectCycle({
                      cycleType: PurchaseOffering.CycleType.RECURRING,
                      cycleDuration: {
                        unit: PurchaseOffering.DurationUnit.YEAR,
                        count: 2,
                      },
                    });
                  }}
                />
              ),
            }}
          />
        </Text>
      </Box>
    </Box>
  );
};
